import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@v2/components/ui/dialog";
import { Input } from "@v2/components/ui/input";
import { supabaseV2 } from "../../lib/supabase";
import { EmptyState, SectionLoading } from "../../components/state";
import { FcIcon } from "../../components/brand";
import { currentIntlLocale } from "../../lib/i18n";
import type { Match } from "../../types";

export function EaImportButton({ match }: { match: Match }) {
  const { t } = useTranslation("mondofc");
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<"all" | "friendlyMatch" | "leagueMatch" | "playoffMatch">("all");
  const [searchQuery, setSearchQuery] = useState("");

  const suggestionsQuery = useQuery({
    queryKey: ["ea-match-suggestions", match.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("suggest_ea_match_for", { _match_id: match.id });
      if (error) throw error;
      return (data || []) as Array<{
        ea_match_id: string;
        played_at: string;
        opponent_name: string;
        match_type: string;
        our_score: number;
        opp_score: number;
        affinity: number;
      }>;
    },
    enabled: open,
  });

  const filtered = (suggestionsQuery.data || []).filter((s) => {
    if (typeFilter !== "all" && s.match_type !== typeFilter) return false;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (s.opponent_name || "").toLowerCase().includes(q);
  });

  const runImport = async (eaMatchId: string, mode: "merge" | "replace") => {
    setImporting(true);
    setMessage(null);
    try {
      const { data, error } = await supabaseV2.rpc("import_ea_match_stats", {
        _match_id: match.id,
        _ea_match_id: eaMatchId,
        _mode: mode,
      });
      if (error) throw error;
      const r = data as any;
      const um = r?.unmatched_names || [];
      setMessage(
        t("eaImport.importedSummary", {
          count: r?.imported || 0,
          s: r?.imported === 1 ? "" : "s",
          presences: r?.presences ? t("eaImport.presencesPart", { count: r.presences }) : "",
          unmatched: um.length > 0 ? t("eaImport.unmatchedPart", { names: um.join(", ") }) : "",
        })
      );
      queryClient.invalidateQueries({ queryKey: ["match-player-stats"] });
      queryClient.invalidateQueries({ queryKey: ["matches"] });
      queryClient.invalidateQueries({ queryKey: ["responses"] });
      queryClient.invalidateQueries({ queryKey: ["session-callups"] });
    } catch (e: any) {
      setMessage(t("eaImport.errorPrefix", { message: e?.message || String(e) }));
    } finally {
      setImporting(false);
    }
  };

  const linked = match.ea_match_id;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-11 sm:h-9 text-sm">
          <FcIcon className="h-3 w-3" />
          {linked ? t("eaImport.reimport") : t("eaImport.import")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("eaImport.title")}</DialogTitle>
          <DialogDescription>
            {t("eaImport.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-full bg-raised p-0.5 text-[13px]">
              {(["all", "friendlyMatch", "leagueMatch", "playoffMatch"] as const).map((k) => {
                const label = k === "all" ? t("eaImport.all") : k === "friendlyMatch" ? t("eaImport.friendly") : k === "leagueMatch" ? t("eaImport.league") : t("eaImport.playoff");
                return (
                  <button
                    key={k}
                    onClick={() => setTypeFilter(k)}
                    className={cn("px-2 py-1 rounded font-medium transition-colors",
                      typeFilter === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("eaImport.searchOpponent")}
                className="h-8 text-xs pl-8"
              />
            </div>
          </div>
          {suggestionsQuery.isLoading && <SectionLoading />}
          {!suggestionsQuery.isLoading && (suggestionsQuery.data || []).length === 0 && (
            <EmptyState text={t("eaImport.emptyNoMatches")} />
          )}
          {!suggestionsQuery.isLoading && (suggestionsQuery.data || []).length > 0 && filtered.length === 0 && (
            <EmptyState text={t("eaImport.emptyNoFilter")} />
          )}
          <div className="max-h-72 overflow-auto space-y-1.5">
            {filtered.map((s) => {
              const isLinked = s.ea_match_id === linked;
              const typeLabel =
                s.match_type === "friendlyMatch"
                  ? t("eaImport.friendly")
                  : s.match_type === "playoffMatch"
                    ? t("eaImport.playoff")
                    : s.match_type === "leagueMatch"
                      ? t("eaImport.league")
                      : "—";
              return (
                <div
                  key={s.ea_match_id}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded border text-sm",
                    isLinked ? "bg-[hsl(var(--ok-soft))]" : "bg-raised"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {s.opponent_name || "—"}{" "}
                      <span className="text-[hsl(var(--faint))] text-[13px] ml-1">
                        {s.played_at ? new Date(s.played_at).toLocaleString(currentIntlLocale(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </p>
                    <p className="text-[13px] text-[hsl(var(--faint))] tabular-nums">
                      {typeLabel} · {s.our_score}-{s.opp_score} · {t("eaImport.affinity")}{" "}
                      <span className={cn(s.affinity >= 0.7 ? "text-emerald-600" : s.affinity >= 0.3 ? "text-amber-600" : "text-rose-600")}>
                        {Math.round(Number(s.affinity) * 100)}%
                      </span>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={isLinked ? "default" : "outline"}
                    className="h-11 sm:h-9 text-sm"
                    disabled={importing}
                    onClick={() => runImport(s.ea_match_id, "merge")}
                  >
                    {isLinked ? t("eaImport.reimportBtn") : t("eaImport.importBtn")}
                  </Button>
                </div>
              );
            })}
          </div>
          {message && (
            <div className="rounded-2xl px-4 py-3 text-sm bg-raised text-muted-foreground">{message}</div>
          )}
          <p className="text-[13px] text-[hsl(var(--faint))]">
            <span dangerouslySetInnerHTML={{ __html: t("eaImport.mergeNote").replace("<0>", '<span class="font-medium text-foreground">').replace("</0>", '</span>') }} />
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={importing}>
            {t("eaImport.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
